/**
 * @fileoverview Game utility functions for the English Acquaintances Game
 * Contains core game mechanics like question generation, answer validation, etc.
 */

import { 
  QUESTION_TYPES, 
  QUESTION_PROMPTS, 
  SPECIAL_VALIDATIONS,
  COMMON_VARIATIONS 
} from './constants';

/**
 * Game Utility Functions
 * 
 * This module provides utility functions for the game logic, including:
 * - Question generation
 * - Answer validation
 * - Game state management
 * 
 * @module utils/gameUtils
 */

/**
 * Generates a set of questions based on the given work object
 * Creates questions for author, publication year, genre, etc.
 * 
 * @param {Object} work - The work object containing literary information
 * @param {string} work.work - The title of the work
 * @param {string} work.author - The author of the work
 * @param {string} work.publication_year - The publication year of the work (as a string)
 * @param {string} work.genre - The genre of the work
 * @param {string} work.age - The literary age/period of the work
 * @returns {Object} Questions array and initial asked questions set
 */
export const generateQuestions = (work) => {
  // Initialize an array with the required 4 standard questions
  const allQuestions = [];
  
  // 1. Author question - always included
  allQuestions.push({
    id: QUESTION_TYPES.AUTHOR,
    type: QUESTION_TYPES.AUTHOR,
    text: QUESTION_PROMPTS[QUESTION_TYPES.AUTHOR] || 'Who is the author of this work?',
    answer: work.author || "Unknown",
    category: 'Author',
    // Add minimalistic question variants
    minimalistic: 'Author?',
    variants: [
      'Who wrote this work?', 
      'Who is the writer of this literary piece?',
      'This work was written by whom?'
    ]
  });
  
  // 2. Year question - always included (match the property name in the JSON)
  allQuestions.push({
    id: QUESTION_TYPES.PUBLICATION_YEAR,
    type: QUESTION_TYPES.PUBLICATION_YEAR,
    text: QUESTION_PROMPTS[QUESTION_TYPES.PUBLICATION_YEAR] || 'In what year was this work published?',
    answer: String(work.publication_year) || "Unknown", // Ensure publication_year is treated as a string
    category: 'Year',
    // Add minimalistic question variants
    minimalistic: 'Publication Year?',
    variants: [
      'When was this work published?',
      'What is the publication year of this work?',
      'This work was published in what year?'
    ]
  });
  
  // 3. Genre question - always included
  allQuestions.push({
    id: QUESTION_TYPES.GENRE,
    type: QUESTION_TYPES.GENRE,
    text: QUESTION_PROMPTS[QUESTION_TYPES.GENRE] || 'What is the genre of this work?',
    answer: work.genre || "Unknown",
    category: 'Genre',
    // Add minimalistic question variants
    minimalistic: 'Genre?',
    variants: [
      'What type of literary work is this?',
      'What category of literature does this belong to?',
      'This work belongs to which literary genre?'
    ]
  });
  
  // 4. Age/Period question - always included
  allQuestions.push({
    id: QUESTION_TYPES.LITERARY_AGE,
    type: QUESTION_TYPES.LITERARY_AGE,
    text: QUESTION_PROMPTS[QUESTION_TYPES.LITERARY_AGE] || 'What literary age/period is this work from?',
    answer: work.age || "Unknown",
    category: 'Period',
    // Add minimalistic question variants
    minimalistic: 'Literary Age?',
    variants: [
      'Which literary period does this work belong to?',
      'What is the literary era of this work?',
      'This work is from which literary age?'
    ]
  });
  
  // Shuffle the questions array to randomize order
  const shuffledQuestions = shuffleArray([...allQuestions]);
  
  // Create a set to track which questions have been asked
  // Add all question types to ensure all 4 questions are always asked
  const initialAskedQuestions = new Set([
    QUESTION_TYPES.AUTHOR,
    QUESTION_TYPES.PUBLICATION_YEAR,
    QUESTION_TYPES.GENRE,
    QUESTION_TYPES.LITERARY_AGE
  ]);
  
  return {
    questions: shuffledQuestions,
    initialAskedQuestions
  };
};

/**
 * Validate a user answer against the correct answer
 * 
 * @param {string} userAnswer - The user's answer
 * @param {string|Array<string>} correctAnswer - The correct answer or array of correct answers
 * @param {boolean} [isMultipleAnswers=false] - Whether multiple answers are valid
 * @returns {boolean} Whether the answer is correct
 */
export const validateAnswer = (userAnswer, correctAnswer, isMultipleAnswers = false) => {
  // Normalize both answers for comparison: lowercase and trim
  const normalizedUserAnswer = userAnswer.toLowerCase().trim();
  
  if (isMultipleAnswers && Array.isArray(correctAnswer)) {
    // Check if the user's answer is in the array of correct answers
    return correctAnswer.some(answer => 
      normalizedUserAnswer === answer.toLowerCase().trim() ||
      normalizedUserAnswer.includes(answer.toLowerCase().trim()) ||
      answer.toLowerCase().trim().includes(normalizedUserAnswer)
    );
  } else {
    // Direct comparison for single answers, also checking for partial matches
    const normalizedCorrectAnswer = correctAnswer.toLowerCase().trim();
    return normalizedUserAnswer === normalizedCorrectAnswer ||
           normalizedUserAnswer.includes(normalizedCorrectAnswer) ||
           normalizedCorrectAnswer.includes(normalizedUserAnswer);
  }
};

/**
 * Shuffle an array using the Fisher-Yates algorithm
 * 
 * @param {Array} array - The array to shuffle
 * @returns {Array} The shuffled array
 */
export const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

/**
 * Choose a question variant - can be verbose or minimalistic
 * 
 * @param {Object} question - The question object
 * @param {boolean} [minimalistic=true] - Whether to use minimalistic format
 * @returns {string} A selected question text variant
 */
export const chooseQuestionVariant = (question, minimalistic = true) => {
  // Safety check for invalid question objects
  if (!question) {
    return "Please answer the following question:";
  }
  
  // If minimalistic mode and minimalistic text is available, use it
  if (minimalistic && question.minimalistic) {
    return question.minimalistic;
  }
  
  // Use default text if no variants are available
  if (!question.variants || question.variants.length === 0) {
    return question.text || `What is the ${question.category || 'answer'}?`;
  }
  
  // Create an array with main text and all variants
  const allVariants = [question.text, ...question.variants];
  
  // Filter out any empty or invalid variants
  const validVariants = allVariants.filter(variant => variant && typeof variant === 'string');
  
  // If no valid variants remain after filtering, return default text
  if (validVariants.length === 0) {
    return `What is the ${question.category || 'answer'}?`;
  }
  
  // Select a random index from the valid variants
  const randomIndex = Math.floor(Math.random() * validVariants.length);
  
  // Return the randomly selected variant
  return validVariants[randomIndex];
};

/**
 * Calculate similarity between two strings
 * Used for fuzzy matching user answers
 * 
 * @param {string} str1 - First string
 * @param {string} str2 - Second string
 * @returns {number} Similarity score between 0 and 1
 */
export const calculateStringSimilarity = (str1, str2) => {
  // Normalize strings
  const a = str1.toLowerCase().trim();
  const b = str2.toLowerCase().trim();
  
  if (a.length === 0 || b.length === 0) return 0;
  if (a === b) return 1;
  
  // Calculate Levenshtein distance
  const matrix = [];
  
  for (let i = 0; i <= b.length; i++) {
    matrix[i] = [i];
  }
  
  for (let j = 0; j <= a.length; j++) {
    matrix[0][j] = j;
  }
  
  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      const cost = a.charAt(j - 1) === b.charAt(i - 1) ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,     // deletion
        matrix[i][j - 1] + 1,     // insertion
        matrix[i - 1][j - 1] + cost  // substitution
      );
    }
  }
  
  const maxLength = Math.max(a.length, b.length);
  return 1 - matrix[b.length][a.length] / maxLength;
};

/**
 * Checks if user answers are correct for the asked questions
 * Handles special validation rules for different types of answers
 * 
 * @param {Object[]} questions - Array of question objects (limited to 4)
 * @param {Object} currentWork - The current literary work
 * @param {Object} userAnswers - User-provided answers by question type
 * @param {Set} askedQuestions - Set of question types that were asked
 * @param {string} subject - The subject code (241 or 242)
 * @returns {Object} Results containing correctness, debug info, etc.
 */
export const checkGameResult = (questions, currentWork, userAnswers, askedQuestions, subject) => {
  const debugResults = {};
  let correctAnswers = 0;
  let answeredQuestions = 0;
  
  // Convert askedQuestions set to array for easier use in debugging
  const askedQuestionsArray = Array.from(askedQuestions);
  
  // Log important information for debugging
  console.log('Checking game result with:');
  console.log('- Questions:', questions.length, 'items');
  console.log('- Asked Questions:', askedQuestionsArray);
  console.log('- User Answers:', userAnswers);
  console.log('- Current Work:', currentWork);
  
  // Process each question to check correctness
  // Note: We're only processing the limited set of 4 questions
  questions.forEach(q => {
    const questionType = q.type;
    const userInput = userAnswers[questionType] || '';
    const correctValue = q.answer || '';
    let isCorrect = false;
    
    // Check if this question was presented to the user
    const wasAsked = askedQuestions.has(questionType);
    
    // Only evaluate questions that were presented to the user
    if (wasAsked) {
      answeredQuestions++;
      
      // Skip evaluation if user didn't provide an answer (empty string)
      if (userInput === '') {
        isCorrect = false;
      } else {
        // Use our enhanced flexible validation system
        isCorrect = flexibleValidateAnswer(userInput, correctValue, questionType);
      }
      
      // Update the count of correct answers
      if (isCorrect) {
        correctAnswers++;
      }
    }
    
    // Store detailed debug info for this question (without questionText)
    debugResults[questionType] = {
      wasAsked,
      userInput,
      correctValue,
      isCorrect
    };
  });
  
  // Determine if all answered questions were correct
  const allCorrect = correctAnswers === answeredQuestions;
  
  return {
    isCorrect: allCorrect,
    correctAnswers,
    answeredQuestions,
    debugInfo: debugResults
  };
};

/**
 * Normalizes text for comparison by:
 * - Converting to lowercase
 * - Removing all punctuation and special characters
 * - Removing extra whitespace
 * - Replacing common character confusions
 * - Handling diacritics and accents
 * 
 * @param {string} text - The text to normalize
 * @returns {string} Normalized text ready for comparison
 */
export const normalizeText = (text) => {
  if (!text) return '';
  
  return String(text)
    .toLowerCase()                        // Convert to lowercase
    .normalize('NFD')                     // Normalize Unicode (decompose accented chars)
    .replace(/[\u0300-\u036f]/g, '')     // Remove diacritics/accents
    .replace(/[.,/#!$%^&*;:{}=\-_`~()'"]/g, ' ') // Replace punctuation with spaces
    .replace(/\s+/g, ' ')                // Replace multiple spaces with single space
    .trim();                             // Remove leading/trailing spaces
};

/**
 * Compares author names with strict matching
 * Only allows exact matches after normalization and last name matches
 * 
 * @param {string} userInput - The user's input
 * @param {string} correctValue - The correct value from data
 * @returns {boolean} Whether the answer is considered correct
 */
export const compareAuthorNames = (userInput, correctValue) => {
  if (!userInput || !correctValue) return false;
  
  // Normalize both inputs
  const normUser = normalizeText(userInput);
  const normCorrect = normalizeText(correctValue);
  
  // Check for exact match after normalization
  if (normUser === normCorrect) return true;
  
  // Handle initials in names (e.g., "A.K Ramanujan" vs "AK Ramanujan" vs "A K Ramanujan")
  const userNoInitialDots = normUser.replace(/\.\s*/g, ' ').replace(/\s+/g, ' ').trim();
  const correctNoInitialDots = normCorrect.replace(/\.\s*/g, ' ').replace(/\s+/g, ' ').trim();
  
  if (userNoInitialDots === correctNoInitialDots) return true;
  
  // Handle name variations (last name only)
  const userParts = normUser.split(' ').filter(Boolean);
  const correctParts = normCorrect.split(' ').filter(Boolean);
  
  // If last name only was provided and it matches
  if (userParts.length === 1 && correctParts.length > 1) {
    const userLastName = userParts[0];
    const correctLastName = correctParts[correctParts.length - 1];
    return userLastName === correctLastName;
  }
  
  // No more fuzzy matching
  return false;
};

/**
 * Compares publication years with exact matching
 * Only allowing normalization for punctuation/whitespace
 * 
 * @param {string} userInput - The user's input
 * @param {string} correctValue - The correct value from data
 * @returns {boolean} Whether the answer is considered correct
 */
export const comparePublicationYears = (userInput, correctValue) => {
  if (!userInput || !correctValue) return false;
  
  // Remove all non-numeric and non-hyphen characters for cleaner comparison
  const cleanInput = String(userInput).replace(/[^\d-]/g, '');
  const cleanCorrect = String(correctValue).replace(/[^\d-]/g, '');
  
  // Direct match after cleaning
  if (cleanInput === cleanCorrect) return true;
  
  // Convert to strings and normalize for other comparisons
  const normUser = normalizeText(String(userInput));
  const normCorrect = normalizeText(String(correctValue));
  
  // Check for exact match after normalization
  if (normUser === normCorrect) return true;
  
  // For year ranges, ensure exact match
  if (String(correctValue).includes('-') && String(userInput).includes('-')) {
    // Extract range values
    const [correctStartStr, correctEndStr] = String(correctValue).split('-');
    const [userStartStr, userEndStr] = String(userInput).split('-');
    
    // Clean and compare
    const cleanCorrectStart = correctStartStr.trim();
    const cleanCorrectEnd = correctEndStr.trim();
    const cleanUserStart = userStartStr.trim();
    const cleanUserEnd = userEndStr.trim();
    
    // Only accept exact range match
    return cleanUserStart === cleanCorrectStart && cleanUserEnd === cleanCorrectEnd;
  }
  
  // No match found
  return false;
};

/**
 * Compares general text values (genres, literary ages) with flexibility
 * Handles special cases for each dataset and common variations
 * 
 * @param {string} userInput - The user's input
 * @param {string} correctValue - The correct value from data
 * @param {string} subject - The subject code (241 or 242)
 * @returns {boolean} Whether the answer is considered correct
 */
export const compareTextValues = (userInput, correctValue, subject) => {
  if (!userInput || !correctValue) return false;
  
  // Normalize both inputs - only ignoring case and minor punctuation
  const normUser = normalizeText(userInput);
  const normCorrect = normalizeText(correctValue);
  
  // Check for exact match after normalization
  if (normUser === normCorrect) return true;
  
  // Check for special validations from constants based on subject
  if (subject && SPECIAL_VALIDATIONS[subject] && SPECIAL_VALIDATIONS[subject][correctValue]) {
    const allowedValues = SPECIAL_VALIDATIONS[subject][correctValue];
    if (allowedValues.includes(normUser)) {
      return true;
    }
  }
  
  // No more flexible matching - must match exactly after case/punctuation normalization
  return false;
};

/**
 * Enhanced flexible answer validation system
 * Accommodates various types of input variations:
 * - Case insensitivity (upper/lowercase)
 * - Ignores punctuation symbols
 * - Handles name variations with flexible spacing/punctuation
 * - Allows partial matches for age/period
 * - Special handling for author names
 * 
 * @param {string} userAnswer - The user's answer
 * @param {string} correctAnswer - The correct answer
 * @param {string} questionType - The type of question (author, age, etc.)
 * @returns {boolean} Whether the answer is correct
 */
export const flexibleValidateAnswer = (userAnswer, correctAnswer, questionType) => {
  // Safety check for empty inputs
  if (!userAnswer || !correctAnswer) {
    return false;
  }
  
  // 1. Normalize both answers: lowercase and trim
  let normalizedUserAnswer = normalizeText(userAnswer);
  let normalizedCorrectAnswer = normalizeText(correctAnswer);
  
  // 2. Specific validation based on question type
  switch (questionType) {
    case QUESTION_TYPES.AUTHOR:
      return validateAuthorName(normalizedUserAnswer, normalizedCorrectAnswer);
      
    case QUESTION_TYPES.PUBLICATION_YEAR:
      // For years, require exact match but remove any non-digit characters
      return validateYear(normalizedUserAnswer, normalizedCorrectAnswer);
      
    case QUESTION_TYPES.LITERARY_AGE:
      return validateLiteraryAge(normalizedUserAnswer, normalizedCorrectAnswer);
      
    case QUESTION_TYPES.GENRE:
      return validateGenre(normalizedUserAnswer, normalizedCorrectAnswer);
      
    default:
      // Fallback to basic comparison with normalized text
      if (normalizedUserAnswer === normalizedCorrectAnswer) {
        return true;
      }
      // Calculate similarity for close matches
      const similarity = calculateStringSimilarity(normalizedUserAnswer, normalizedCorrectAnswer);
      return similarity > 0.7; // General threshold for other question types
  }
};

/**
 * Validate author names with special handling for initials and spacing
 * Handles cases like "A. K. Ramanujan" vs "a k ramanujan" vs "ak ramanujan"
 * 
 * @param {string} userInput - Normalized user input
 * @param {string} correctValue - Normalized correct value
 * @returns {boolean} Whether the answer is correct
 */
export const validateAuthorName = (userInput, correctValue) => {
  // Direct match after normalization
  if (userInput === correctValue) {
    return true;
  }
  
  // Create special normalized versions for initial comparison
  // This removes ALL spaces and dots for comparison
  const userCompact = userInput.replace(/[\s.]/g, '');
  const correctCompact = correctValue.replace(/[\s.]/g, '');
  
  // If compact versions match (ignoring spaces and dots), accept it
  if (userCompact === correctCompact) {
    return true;
  }
  
  // Split names into components (first name, middle initial, last name)
  const userParts = userInput.split(' ').filter(part => part.length > 0);
  const correctParts = correctValue.split(' ').filter(part => part.length > 0);
  
  // Try to extract last name (most important part to match)
  const userLastName = userParts.length > 0 ? userParts[userParts.length - 1] : '';
  const correctLastName = correctParts.length > 0 ? correctParts[correctParts.length - 1] : '';
  
  // If last names don't match, the answer is likely wrong
  // Require surname to match if it's substantial (not just an initial)
  if (userLastName !== correctLastName && userLastName.length > 1 && correctLastName.length > 1) {
    // Calculate similarity for possible typos in last name
    const lastNameSimilarity = calculateStringSimilarity(userLastName, correctLastName);
    if (lastNameSimilarity < 0.8) {
      return false;
    }
  }
  
  // Extract initials from both strings
  let userInitials = '';
  let correctInitials = '';
  
  // Get user initials (first letter of each part except the last name)
  for (let i = 0; i < userParts.length - 1; i++) {
    if (userParts[i].length > 0) {
      userInitials += userParts[i].charAt(0);
    }
  }
  
  // Get correct initials (first letter of each part except the last name)
  for (let i = 0; i < correctParts.length - 1; i++) {
    if (correctParts[i].length > 0) {
      correctInitials += correctParts[i].charAt(0);
    }
  }
  
  // Special case for "A.K. Ramanujan" type names
  if (correctInitials.length > 0) {
    // If user has the correct initials (even if spacing/dots are different) + last name, accept it
    if (userInitials === correctInitials && userLastName === correctLastName) {
      return true;
    }
    
    // Accept if the user has given only the last name, which is substantial
    // (For cases where user just types "Ramanujan")
    if (userParts.length === 1 && userLastName === correctLastName && correctLastName.length > 1) {
      return true;
    }
    
    // Handle case where user types initials without spaces "AK Ramanujan"
    if (userParts.length === 2) {
      const firstPart = userParts[0];
      // If the first part contains the correct initials (squashed together)
      if (firstPart.length === correctInitials.length && 
          firstPart.split('').every((char, i) => char === correctInitials.charAt(i)) &&
          userLastName === correctLastName) {
        return true;
      }
    }
  }
  
  // Calculate overall similarity for close matches
  const similarity = calculateStringSimilarity(userInput, correctValue);
  return similarity > 0.75; // High threshold for author names
};

/**
 * Validate publication years, allowing for flexible formatting
 * 
 * @param {string} userInput - Normalized user input
 * @param {string} correctValue - Normalized correct value
 * @returns {boolean} Whether the answer is correct
 */
export const validateYear = (userInput, correctValue) => {
  // Extract only digits from both inputs
  const userYearDigits = userInput.replace(/\D/g, '');
  const correctYearDigits = correctValue.replace(/\D/g, '');
  
  // Direct match on digits
  if (userYearDigits === correctYearDigits) {
    return true;
  }
  
  // Calculate similarity for close matches
  const similarity = calculateStringSimilarity(userYearDigits, correctYearDigits);
  return similarity > 0.9; // High threshold for years to allow for single digit typos
};

/**
 * Validate literary age with special handling for partial matches
 * Handles cases like "Post Independence Age" vs "Post Independence"
 * 
 * @param {string} userInput - Normalized user input
 * @param {string} correctValue - Normalized correct value
 * @returns {boolean} Whether the answer is correct
 */
export const validateLiteraryAge = (userInput, correctValue) => {
  // Direct match after normalization
  if (userInput === correctValue) {
    return true;
  }
  
  // Handle "Post Independence Age" vs "Post Independence" case
  if (correctValue.includes('age') && !userInput.includes('age')) {
    // If user omitted "age" but rest matches, accept it
    const baseAge = correctValue.replace(/\s*age\s*$/i, '').trim();
    if (userInput === baseAge) {
      return true;
    }
  }
  
  // Handle special cases from SPECIAL_VALIDATIONS
  // Iterate through all subjects
  for (const subject in SPECIAL_VALIDATIONS) {
    const validations = SPECIAL_VALIDATIONS[subject];
    
    // Check if the correct value has special validations
    if (validations[correctValue]) {
      // Check if user input matches any of the special validations
      const specialValidations = validations[correctValue];
      if (specialValidations.includes(userInput)) {
        return true;
      }
    }
  }
  
  // Special case: If correct answer is "Age of X" and user just entered "X"
  if (correctValue.toLowerCase().startsWith('age of ')) {
    const ageName = correctValue.toLowerCase().replace('age of ', '').trim();
    if (userInput === ageName) {
      return true;
    }
  }
  
  // Handle common literary age variations
  // Check if either the correct answer or user input is a known variation
  for (const key in COMMON_VARIATIONS) {
    const variations = COMMON_VARIATIONS[key];
    
    // If correct value matches this key or one of its variations
    if (key === correctValue || variations.includes(correctValue)) {
      // Check if user input matches the key or one of its variations
      if (key === userInput || variations.includes(userInput)) {
        return true;
      }
    }
  }
  
  // Special handling for Post Independence Age
  if (correctValue.toLowerCase() === 'post independence age') {
    // Accept "post independence" but reject just "independence"
    if (userInput.toLowerCase() === 'post independence') {
      return true;
    }
    
    // Reject if only "independence" was provided
    if (userInput.toLowerCase() === 'independence') {
      return false;
    }
  }
  
  // Calculate similarity for close matches
  const similarity = calculateStringSimilarity(userInput, correctValue);
  return similarity > 0.7; // Lower threshold for literary ages
};

/**
 * Validate genre with support for common variations
 * 
 * @param {string} userInput - Normalized user input
 * @param {string} correctValue - Normalized correct value
 * @returns {boolean} Whether the answer is correct
 */
export const validateGenre = (userInput, correctValue) => {
  // Direct match after normalization
  if (userInput === correctValue) {
    return true;
  }
  
  // Handle genre variations from COMMON_VARIATIONS
  for (const baseForm in COMMON_VARIATIONS) {
    const variations = COMMON_VARIATIONS[baseForm];
    
    // Check if correct answer is in variations or is the base form
    if (correctValue === baseForm || variations.includes(correctValue)) {
      // Check if user input is the base form or in variations
      if (userInput === baseForm || variations.includes(userInput)) {
        return true;
      }
    }
  }
  
  // Calculate similarity for close matches
  const similarity = calculateStringSimilarity(userInput, correctValue);
  return similarity > 0.7; // Medium threshold for genres
};

/**
 * Utility function for testing the flexible validation system
 * Can be used in the browser console for testing various inputs
 * 
 * @param {string} userInput - The user's input to test
 * @param {string} correctValue - The correct value from data
 * @param {string} questionType - The question type (author, publication_year, genre, age)
 * @returns {boolean} Whether the answer is considered correct
 */
export const testFlexibleValidation = (userInput, correctValue, questionType) => {
  console.log(`Testing validation for type: ${questionType}`);
  console.log(`User input: "${userInput}"`);
  console.log(`Correct value: "${correctValue}"`);
  
  // Normalize both inputs for display
  const normalizedUser = normalizeText(userInput);
  const normalizedCorrect = normalizeText(correctValue);
  
  console.log(`Normalized user input: "${normalizedUser}"`);
  console.log(`Normalized correct value: "${normalizedCorrect}"`);
  
  // Perform the validation
  const isValid = flexibleValidateAnswer(userInput, correctValue, questionType);
  
  console.log(`Validation result: ${isValid ? 'CORRECT ✅' : 'INCORRECT ❌'}`);
  return isValid;
};

/**
 * Utility function for batch testing multiple validation cases at once
 * Useful for regression testing when modifying the validation functions
 * 
 * @param {Array<Object>} testCases - Array of test cases to run
 * @returns {Object} Test results with counts and detailed results
 */
export const batchTestValidation = (testCases) => {
  const results = {
    total: testCases.length,
    passed: 0,
    failed: 0,
    details: []
  };
  
  testCases.forEach((testCase, index) => {
    const { input, correct, type, expectation = true, description } = testCase;
    
    // Run the validation
    const isValid = flexibleValidateAnswer(input, correct, type);
    const testPassed = (isValid === expectation);
    
    // Update counts
    if (testPassed) {
      results.passed++;
    } else {
      results.failed++;
    }
    
    // Add detailed result
    results.details.push({
      test: index + 1,
      description: description || `Test ${index + 1}`,
      input,
      correct,
      type,
      expected: expectation,
      actual: isValid,
      passed: testPassed
    });
  });
  
  console.table(results.details);
  console.log(`Test Summary: ${results.passed}/${results.total} tests passed (${Math.round(results.passed/results.total*100)}%)`);
  
  return results;
};

// Example test cases for the validation system
export const EXAMPLE_TEST_CASES = [
  // Author test cases
  { input: 'A.K. Ramanujan', correct: 'A.K Ramanujan', type: QUESTION_TYPES.AUTHOR, description: 'Author with extra period' },
  { input: 'a k ramanujan', correct: 'A.K Ramanujan', type: QUESTION_TYPES.AUTHOR, description: 'Author with lowercase and no periods' },
  { input: 'ak ramanujan', correct: 'A.K Ramanujan', type: QUESTION_TYPES.AUTHOR, description: 'Author with no spaces or periods' },
  { input: 'Ramanujan', correct: 'A.K Ramanujan', type: QUESTION_TYPES.AUTHOR, description: 'Author with last name only' },
  { input: 'AK RAMANUJAN', correct: 'A.K Ramanujan', type: QUESTION_TYPES.AUTHOR, description: 'Author with all caps' },
  
  // Literary Age test cases
  { input: 'Post Independence', correct: 'Post Independence Age', type: QUESTION_TYPES.LITERARY_AGE, description: 'Age without "Age" suffix' },
  { input: 'Post-Independence Age', correct: 'Post Independence Age', type: QUESTION_TYPES.LITERARY_AGE, description: 'Age with hyphen' },
  { input: 'independence', correct: 'Post Independence Age', type: QUESTION_TYPES.LITERARY_AGE, description: 'Just "independence"', expectation: false },
  { input: 'Dryden', correct: 'Age of Dryden', type: QUESTION_TYPES.LITERARY_AGE, description: 'Age of X simplified to just X' },
  
  // Year test cases
  { input: '1.9.7.6', correct: '1976', type: QUESTION_TYPES.PUBLICATION_YEAR, description: 'Year with dots' },
  { input: '(1976)', correct: '1976', type: QUESTION_TYPES.PUBLICATION_YEAR, description: 'Year with parentheses' },
  
  // Genre test cases
  { input: 'poem collection', correct: 'Collection of Poems', type: QUESTION_TYPES.GENRE, description: 'Alternative genre name' },
  { input: 'anthology: poems', correct: 'Collection of Poems', type: QUESTION_TYPES.GENRE, description: 'Genre with punctuation' }
]; 